import { readContentDetails, readContentType } from "./discoverContent";
import { APP_USAGE_NOTICES } from "./discoveryEditorial";
import { getLocale } from "@/i18n/locale";
import { isExecutionClass } from "@/components/compute/appAccess";
import type { DiscoverApp } from "../discoverClient";
import type { MyAppsAuthState, MyAppsState } from "../myAppsStore";
import type { PresetDiscoverApp } from "../presetApps";
import type { PersonalApp } from "../types";
import { DISCOVERY_THEMES } from "./discoveryThemes";
import { detailsNavigationTarget, parseDiscoverySelection } from "./discoveryUrl";
import type {
  DiscoveryCardModel,
  DiscoveryCardStats,
  DiscoverySelection,
  DiscoverySort,
  DiscoveryTheme,
} from "./types";

/** Keep compact views to the first supplied paragraph; full text remains searchable. */
export function compactAppDescription(description: string): string {
  return description.split(/\n\s*\n/, 1)[0].replace(/\s+/g, " ").trim();
}

/** Compact display only; accessible labels retain the full measured count. */
export function formatDiscoveryCount(value: number | null | undefined): string {
  return typeof value === "number"
    ? new Intl.NumberFormat(getLocale(), { notation: "compact" }).format(value).replace("K", "k")
    : "—";
}

function countOrNull(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : null;
}

function statsOf(app?: DiscoverApp): DiscoveryCardStats {
  const stats = app?.stats;
  const asOf = stats?.as_of;
  return {
    discoverRunCount: countOrNull(stats?.discover_run_count),
    installCount: countOrNull(stats?.install_count),
    runCount: countOrNull(stats?.run_count),
    ...(stats?.run_count_status === "recorded" || stats?.run_count_status === "not_tracked"
      ? { runCountStatus: stats.run_count_status } : {}),
    commentCount: countOrNull(stats?.comment_count),
    likeCount: countOrNull(stats?.like_count),
    asOf: asOf && Number.isFinite(Date.parse(asOf)) ? asOf : null,
  };
}

function publicPreviewUrl(app: DiscoverApp): string | null {
  if (app.execution_class !== "Browser" || !app.public_preview_url) return null;
  try {
    const url = new URL(app.public_preview_url);
    return url.protocol === "https:" && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export function currentAiRating(app: DiscoverApp): DiscoverApp["ai_rating"] {
  const value = app.ai_rating;
  if (!value || !app.published_version || value.version_ref !== app.published_version ||
    !Number.isSafeInteger(value.rated_count) || value.rated_count < 0 ||
    (value.rated_count === 0 ? value.average !== null :
      typeof value.average !== "number" || !Number.isFinite(value.average) || value.average < 1 || value.average > 5)) return undefined;
  return value;
}

/** IDs keep their namespaces; equal names never imply the same application. */
export function normalizeDiscoveryCatalogue(
  apps: readonly DiscoverApp[],
  presets: readonly PresetDiscoverApp[],
): DiscoveryCardModel[] {
  const entries: DiscoveryCardModel[] = [];
  for (const app of apps) {
    const selection = parseDiscoverySelection(`discover:${app.id}`);
    if (!selection || selection.kind !== "discover") continue;
    const executionClass = isExecutionClass(app.execution_class)
      ? app.execution_class
      : null;
    const viewer = app.viewer;
    entries.push({
      publicTrialPolicy: app.public_trial,
      source: app.source,
      publishedVersion: app.published_version,
      aiRating: currentAiRating(app),
      recordedPreview: validRecordedPreview(app),
      developers: Array.isArray(app.developers) ? app.developers.filter((developer) => {
        if (!developer || typeof developer.id !== "string" || typeof developer.name !== "string" || !developer.name) return false;
        if (typeof developer.profile_url !== "string") return false;
        if (developer.profile_url.startsWith("/developers/")) return /^\/developers\/[^/?#]+$/.test(developer.profile_url);
        try { const url = new URL(developer.profile_url); return url.protocol === "https:" && !url.username && !url.password; } catch { return false; }
      }) : [],
      distribution: app.source_kind === "community" ? "Community edition" : app.source_kind === "oss" ? "Store edition" : undefined,
      selection,
      title: app.title,
      description: app.description,
      usageNotice: APP_USAGE_NOTICES[app.id],
      iconUrl: app.icon,
      thumbnailUrl: app.thumbnail_url,
      detailPath: detailsNavigationTarget(selection),
      externalUrl: null,
      previewUrl: publicPreviewUrl(app),
      publicAccessAvailable: app.public_access_available === true,
      category: app.category ?? null,
      contentType: readContentType(app.content_type),
      contentDetails: readContentDetails(app.content_details),
      publishedAt: app.published_at,
      executionClass,
      publicTryAvailable:
        app.public_try_available === true && executionClass !== null,
      tryFirstAction:
        app.public_try_available === true &&
        typeof app.try_first_action === "string"
          ? app.try_first_action
          : null,
      added: app.added,
      stats: statsOf(app),
      viewer:
        viewer &&
        typeof viewer.liked === "boolean" &&
        typeof viewer.can_like === "boolean"
          ? { liked: viewer.liked, canLike: viewer.can_like }
          : null,
    });
  }
  for (const preset of presets) {
    const selection = parseDiscoverySelection(`preset:${preset.id}`);
    if (!selection || selection.kind !== "preset") continue;
    entries.push({
      selection,
      title: preset.title,
      description: preset.description,
      iconUrl: preset.icon,
      thumbnailUrl: preset.thumbnail_url,
      detailPath: detailsNavigationTarget(selection),
      externalUrl: preset.app_url,
      previewUrl: null,
      publishedAt: null,
      executionClass: null,
      publicTryAvailable: false,
      added: preset.added,
      stats: statsOf(),
      viewer: null,
    });
  }
  return entries;
}

export function validRecordedPreview(app: DiscoverApp): DiscoverApp["preview_video"] {
  const preview = app.preview_video;
  if (!preview || !app.published_version || preview.published_version !== app.published_version ||
    !["video/mp4", "video/webm"].includes(preview.content_type) ||
    !Number.isSafeInteger(preview.duration_ms) || preview.duration_ms <= 0 || preview.duration_ms > 30_000 ||
    !Number.isSafeInteger(preview.byte_size) || preview.byte_size <= 0 || preview.byte_size > 4_194_304 ||
    !Number.isSafeInteger(preview.width) || preview.width <= 0 || preview.width > 1920 ||
    !Number.isSafeInteger(preview.height) || preview.height <= 0 || preview.height > 1920) return null;
  try {
    for (const value of [preview.url, preview.poster_url]) {
      const url = new URL(value);
      if (url.protocol !== "https:" || url.username || url.password) return null;
    }
    return preview;
  } catch { return null; }
}

export function visibleDiscoveryThemes(
  entries: readonly DiscoveryCardModel[],
  themes: readonly DiscoveryTheme[] = DISCOVERY_THEMES,
): DiscoveryTheme[] {
  const listingIds = new Set(
    entries
      .filter((entry) => entry.selection.kind === "discover")
      .map((entry) => entry.selection.id),
  );
  return themes.filter((theme) =>
    theme.listingIds.some((id) => listingIds.has(id)),
  );
}

export function filterDiscoveryCatalogue(
  entries: readonly DiscoveryCardModel[],
  filter: { query: string; themeId: string | null; sort: DiscoverySort },
  themes: readonly DiscoveryTheme[] = DISCOVERY_THEMES,
): DiscoveryCardModel[] {
  const availableThemes = visibleDiscoveryThemes(entries, themes);
  const theme = availableThemes.find(
    (candidate) => candidate.id === filter.themeId,
  );
  const tokens = filter.query
    .toLocaleLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const matching = entries.filter((entry) => {
    const isListing = entry.selection.kind === "discover";
    if (
      theme &&
      (!isListing || !theme.listingIds.includes(entry.selection.id))
    ) {
      return false;
    }
    const labels = isListing
      ? availableThemes
          .filter((candidate) =>
            candidate.listingIds.includes(entry.selection.id),
          )
          .map((candidate) => `${candidate.id} ${candidate.label}`)
          .join(" ")
      : "";
    const haystack =
      `${entry.title} ${entry.description} ${labels}`.toLocaleLowerCase();
    return tokens.every((token) => haystack.includes(token));
  });
  if (filter.sort === "new") {
    return matching.sort((a, b) => publishedTime(b) - publishedTime(a));
  }
  if (theme) {
    return matching.sort(
      (a, b) =>
        theme.listingIds.indexOf(a.selection.id) -
        theme.listingIds.indexOf(b.selection.id),
    );
  }
  return matching;
}

function publishedTime(entry: DiscoveryCardModel): number {
  const timestamp = entry.publishedAt ? Date.parse(entry.publishedAt) : NaN;
  return Number.isFinite(timestamp) ? timestamp : -Infinity;
}

export interface DiscoveryLibraryScope {
  userId: string | null;
  authState: MyAppsAuthState;
  snapshot: Pick<MyAppsState, "userId" | "instances">;
}

export type ResolvedDiscoverySelection =
  | { kind: "catalogue"; entry: DiscoveryCardModel }
  | { kind: "instance"; instance: PersonalApp };

/** Resolve against the full catalogue, never the current search results. */
export function resolveDiscoverySelection(
  selection: DiscoverySelection | null,
  catalogue: readonly DiscoveryCardModel[],
  library: DiscoveryLibraryScope,
): ResolvedDiscoverySelection | null {
  if (!selection) return null;
  if (selection.kind !== "instance") {
    const entry = catalogue.find(
      (candidate) =>
        candidate.selection.kind === selection.kind &&
        candidate.selection.id === selection.id,
    );
    return entry ? { kind: "catalogue", entry } : null;
  }
  if (
    library.authState !== "verified" ||
    !library.userId ||
    library.snapshot.userId !== library.userId
  ) {
    return null;
  }
  const instance = library.snapshot.instances?.find(
    (app) =>
      app.instance_id === selection.id &&
      app.status === "active" &&
      app.public_try_expires_at == null,
  );
  return instance ? { kind: "instance", instance } : null;
}
